import { Bug, MonitorSmartphone } from "lucide-react";
import { useMemo, useState } from "react";
import { useApp } from "@/lib/AppContext";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { loadConfig } from "@/lib/config";

/**
 * UI-13's Advanced section, and ARCH-8's one control.
 *
 * Developer mode is a stored preference (`user_settings.developer_mode`). Turning it on reveals the Debug tab on a
 * recording — per-step timings, the model that actually ran, and the input and output
 * artifacts — which is the whole of ARCH-8 on this side.
 *
 * The row below it is what the handoff calls "installation info" — which Server this
 * browser is talking to is the first thing anybody debugging a deployment wants to know,
 * and it has never been visible anywhere else.
 */
export function AdvancedSettings({ summary }: { summary: string }) {
  const { settings, updateSettings } = useApp();

  // Read here rather than threaded through context: it is the same pure read of
  // `window.__FACTORIUM_CONFIG__` the app makes at start-up, and this is the only screen
  // that shows it.
  const resolved = useMemo(loadConfig, []);
  const endpoints = resolved.ok ? resolved.config : null;

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const developerMode = settings?.developerMode ?? false;

  async function toggle() {
    if (busy) return;

    setBusy(true);
    setProblem(null);

    try {
      await updateSettings({ developerMode: !developerMode });
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That preference could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Advanced
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div className="flex min-w-0 gap-[var(--space-3)]">
            <Bug size={18} strokeWidth={2} className="mt-[2px] shrink-0 text-[var(--text-secondary)]" />
            <div className="min-w-0">
              <p className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
                Developer mode
              </p>
              <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                Adds a Debug tab to every recording: what each step was given, what it produced, how
                long it took, which model actually ran, and the recording's own identifier.
              </p>
            </div>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={developerMode}
            aria-label="Developer mode"
            disabled={busy}
            onClick={() => void toggle()}
            className={cn(
              "relative h-[22px] w-[38px] shrink-0 rounded-[var(--radius-full)] transition-colors disabled:opacity-50",
              developerMode ? "bg-[var(--primary)]" : "bg-[var(--surface-2)]",
            )}
          >
            <span
              className={cn(
                "absolute top-[3px] h-4 w-4 rounded-[var(--radius-full)] bg-[var(--text-inverse)] transition-[left]",
                developerMode ? "left-[19px]" : "left-[3px]",
              )}
            />
          </button>
        </div>
      </section>

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
        <div className="flex gap-[var(--space-3)]">
          <MonitorSmartphone size={16} strokeWidth={2} className="mt-[2px] shrink-0 text-[var(--text-secondary)]" />
          <div className="min-w-0 flex-1">
            <p className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
              This installation
            </p>

            <dl className="mt-[var(--space-3)] grid grid-cols-[auto_1fr] gap-x-[var(--space-4)] gap-y-1">
              <Fact label="Server" value={endpoints?.apiBaseUrl ?? "not configured"} />
              <Fact label="Sign-in" value={endpoints?.supabaseUrl ?? "not configured"} />
            </dl>

            <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
              Read from the container's environment at start-up, not baked into this bundle — so a
              deployment can be repointed without a rebuild.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </dt>
      <dd className="truncate font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
        {value}
      </dd>
    </>
  );
}
